import React, { useEffect, useState } from 'react';
import LoadingModal from '../loading_modal';
import Form, {
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { SectionTitle } from '@grnhse/seedling/lib/birch/typography';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import EmailIcon from '@grnhse/seedling/lib/birch/icons/email';
import { COUNT_FOR_WARNING as BULK_THRESHOLD_COUNT } from 'people/legacy/index/bulk_actions/utils';
import { useFetchModal } from '../../shared/form/useFetchModal';
import { ModalBodyWrapper } from '../../shared/form/Fields';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { standardRequestWithErrorFlash } from '../../../shared/utils/request';
import RejectFormBody from './reject_form_body';
import { RejectModalOptionsResponse } from '../../shared/types';
import { withLocalePrefix } from '../../../shared/utils/translation';
import { serializeCustomFieldsPayload } from '../../shared/form/CustomField';
import { format } from 'date-fns';
import { parseDateString } from 'candidate_profile_redesign/shared/utils';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import BulkRejectConfirmationModal from './bulk_reject_confirmation_modal';
import ConditionalTooltip from 'candidate_profile_redesign/shared/conditional_tooltip';
import classNames from 'classnames';

const t = withLocalePrefix('candidate_profile_redesign.modals');

export type RejectionCategory = 'default' | 'security_risk';

export interface RejectForm {
  to: Item;
  from: Item;
  subject: string;
  html_body: string;
  cc: Item[];
  cc_recruiter: boolean;
  cc_coordinator: boolean;
  include_self: boolean;
  template: Item;
  reject_from_all: boolean;
  rejection_reason?: Item;
  currentDefaultRejectValue?: Item;
  rejection_category?: RejectionCategory;
  on_other_active_hiring_plans: boolean;
  custom_rejection_type?: Item;
  custom_rejection_reason?: string;
  notes_to_agency?: string;
  start_new_prospect_process?: boolean;
  mark_as_do_not_email?: boolean;
  confirmation_phrase?: string;
  rejection_note: string;
  send_email: boolean;
  sendDate?: Date;
  when?: Item;
  time?: Item;
  default_attachments?: Item[];
  application_ids?: number[];
}

type RejectModalProps = {
  bulkMode: boolean;
  candidateName?: string;
  isProspect?: boolean;
  getRejectModalPath: string;
  redirectPath?: string;
  changeApplicationStatePath?: string;
  openMakeProspectModal?: () => void;
  isRejectModalOpen: boolean;
  closeRejectModal: () => void;
  applicationsToRejectInBulkCount?: number;
  onRejectSuccess?: () => void;
  rejectionTrackingPayload?: Record<string, unknown>;
  rejectionCategory?: RejectionCategory;
};

export const formatPayload = (
  form: RejectForm,
  spamReasonId = '',
  dateFormat = '',
  hiringPlanId: number | undefined,
  inProcessStageId: number | undefined,
  bulkMode: boolean,
  applicationsToRejectInBulkCount = 0,
  applicationId?: number
  // eslint-disable-next-line max-params
) => {
  const payload: {
    action_type: string;
    reject_from_all: string;
    on_other_active_hiring_plans: string;
    rejection_reason?: string;
    custom_rejection_reason_type?: string;
    custom_rejection_reason?: string;
    rejection_note: string;
    rejection_details: ReturnType<typeof serializeCustomFieldsPayload>;
    agency_note?: string;
    send_rejection_email: boolean;
    mark_as_do_not_email?: boolean;
    application_ids?: number[];
    applicationId?: number;
    confirmation_number?: string;
    query?: {
      hiring_plan_id?: string;
      job_status: 'closed';
      stage_status_id?: number;
    };
  } = {
    action_type:
      form.rejection_reason?.value === spamReasonId
        ? 'reject_as_spam'
        : form.reject_from_all
        ? 'reject_all'
        : 'reject',
    reject_from_all: form.reject_from_all ? 'true' : 'false',
    on_other_active_hiring_plans: form.on_other_active_hiring_plans
      ? 'true'
      : 'false',
    rejection_reason: form.rejection_reason?.value,
    custom_rejection_reason_type: form.custom_rejection_type?.value,
    custom_rejection_reason: form.custom_rejection_reason,
    rejection_note: form.rejection_note || '',
    rejection_details: serializeCustomFieldsPayload(form),
    agency_note: form.notes_to_agency,
    send_rejection_email: form.send_email,
    mark_as_do_not_email: form.mark_as_do_not_email,
    application_ids: form.application_ids,
    applicationId: applicationId,
  };

  if (bulkMode) {
    payload.confirmation_number = applicationsToRejectInBulkCount.toString();
    payload.query = {
      hiring_plan_id: hiringPlanId?.toString(),
      job_status: 'closed',
      stage_status_id: inProcessStageId,
    };
  }

  const sendDate = form.sendDate ? format(form.sendDate, dateFormat) : '';
  const emailPayload = form.send_email
    ? {
        rejection_from: form.from.value,
        rejection_to: form.to.value,
        rejection_cc: form.cc
          ?.map((option: Item) =>
            option.userCreated ? option.label : option.value
          )
          .join(','),
        rejection_cc_recruiter: form.cc_recruiter,
        rejection_cc_coordinator: form.cc_coordinator,
        rejection_include_self: form.include_self,
        rejection_subject: form.subject,
        rejection_html_body: form.html_body,
        rejection_send_at: form.when?.value,
        rejection_email_date: sendDate,
        rejection_email_time: form.time?.value ?? '',
        rejection_email_template_id: form.template?.value?.match(
          /email_templates\/(\d+)/
        )?.[1],
        custom_attachment_url: form.default_attachments
          ?.map((a) => a.value)
          .join(','),
        custom_attachment_filename: form.default_attachments
          ?.map((a) => a.value)
          .join(','),
      }
    : {};

  if (bulkMode) {
    delete emailPayload.rejection_to;
  }

  return { ...payload, ...emailPayload };
};

const ModalFooter = ({
  canSendEmail,
  submitting,
  closeRejectModal,
  cannotEmailReason,
}: {
  canSendEmail: boolean | undefined;
  submitting: boolean;
  closeRejectModal: () => void;
  cannotEmailReason?: string;
}) => {
  const { watch, setValue } = useFormContext<RejectForm>();

  const sendEmail = watch('send_email');
  const when = watch('when');
  const rejectionCategory = watch('rejection_category');

  const isSecurityRisk = rejectionCategory === 'security_risk';
  const willSendNow = when ? when.value === 'now' : false;

  return (
    <CompactRow justifyContent="space-between" sx={{ margin: 0 }}>
      <SecondaryButton onClick={closeRejectModal} data-provides="cancel-reject">
        {t('reject.cancel')}
      </SecondaryButton>
      <CompactRow sx={{ margin: 0 }}>
        <PrimaryButton
          loading={!sendEmail && submitting}
          disabled={submitting}
          type="submit"
          data-provides="submit-reject"
          onClick={() => {
            setValue('send_email', false);
          }}
        >
          {isSecurityRisk
            ? t('reject.reject_as_security_risk')
            : t('reject.reject')}
        </PrimaryButton>
        {!isSecurityRisk && (
          <ConditionalTooltip
            when={!canSendEmail}
            label={cannotEmailReason || ''}
          >
            <PrimaryButton
              icon={EmailIcon}
              loading={sendEmail && submitting}
              disabled={submitting || !canSendEmail}
              type="submit"
              data-provides="submit-reject-with-email"
              onClick={() => {
                setValue('send_email', true);
              }}
            >
              {willSendNow
                ? t('reject.reject_with_email')
                : t('reject.reject_with_scheduled_email')}
            </PrimaryButton>
          </ConditionalTooltip>
        )}
      </CompactRow>
    </CompactRow>
  );
};

const RejectModal = (props: RejectModalProps) => {
  const {
    bulkMode,
    applicationsToRejectInBulkCount = 0,
    candidateName = '',
    isProspect = false,
    getRejectModalPath,
    redirectPath,
    changeApplicationStatePath = '',
    openMakeProspectModal = () => {
      return;
    },
    isRejectModalOpen,
    closeRejectModal,
    onRejectSuccess,
    rejectionTrackingPayload,
  } = props;

  const [submitting, setSubmitting] = useState<boolean>(false);

  const [
    isConfirmationModalOpen,
    openConfirmationModal,
    closeConfirmationModal,
  ] = useModal();

  const { data, loading } = useFetchModal<RejectModalOptionsResponse>(
    getRejectModalPath,
    isRejectModalOpen
  );

  const canSendCandidateEmail = data?.email?.canSend;
  const canSendEmail = canSendCandidateEmail || bulkMode;
  const cannotEmailReason = data?.email?.cannotEmailReason;

  useEffect(() => {
    if (bulkMode && data) {
      data.email.sendAt.whenEmailOptions =
        data.email.sendAt.whenEmailOptions.filter((o) => o.value !== 'now');

      if (data.email.sendAt.defaultWhenOption?.value === 'now') {
        data.email.sendAt.defaultWhenOption = null;
      }
    }
  }, [data, bulkMode]);

  const onModalSubmit = async (formData: RejectForm) => {
    const basePayload = formatPayload(
      formData,
      data?.rejectionReasons?.spamReason?.value,
      data?.dateFormatBirchPicker,
      data?.hiringPlanId,
      data?.inProcessStageId,
      bulkMode,
      applicationsToRejectInBulkCount,
      rejectionTrackingPayload?.applicationId as number | undefined
    );

    const payload: Record<string, unknown> = {
      ...(basePayload as object),
      ...(rejectionTrackingPayload || {}),
    };

    const successMessage = bulkMode
      ? t('reject.bulk.success', {
          count: applicationsToRejectInBulkCount,
        })
      : t(`reject.success.${isProspect ? 'prospect' : 'candidate'}`);

    setSubmitting(true);
    try {
      const res = await standardRequestWithErrorFlash.post(
        bulkMode ? data?.bulkRejectCandidatesPath : changeApplicationStatePath,
        payload,
        { successMessage }
      );
      if (res.data.status === 'success') {
        if (formData.start_new_prospect_process) {
          closeRejectModal();
          openMakeProspectModal();
        } else if (bulkMode && redirectPath) {
          window.location.pathname = redirectPath;
        } else if (onRejectSuccess) {
          onRejectSuccess();
          setSubmitting(false);
        } else {
          window.location.reload();
        }
      } else {
        setSubmitting(false);
      }
    } catch {
      setSubmitting(false);
    }
  };

  const title = bulkMode
    ? t('reject.bulk.title', {
        count: applicationsToRejectInBulkCount,
      })
    : t(`reject.title.${isProspect ? 'prospect' : 'candidate'}`, {
        name: candidateName,
      });
  const shouldMaskTitle =
    Boolean(candidateName) && title.includes(candidateName);

  if (loading) {
    return (
      <LoadingModal
        submitButtonText={t('reject.reject')}
        secondSubmitButtonText={t('reject.reject_with_email')}
        secondSubmitButtonIcon={EmailIcon}
        type="two-submit-buttons"
        modalTitle={title}
        maskTitle={shouldMaskTitle}
      />
    );
  }

  if (!data) {
    return null;
  }

  const handleOnModalSubmit = async (formData: RejectForm) => {
    if (bulkMode && applicationsToRejectInBulkCount >= BULK_THRESHOLD_COUNT) {
      openConfirmationModal();
    } else {
      onModalSubmit(formData);
    }
  };

  // if we get a rejection category prop and can use security risk rejection, use the prop
  // otherwise, default to security risk if fraud report is high risk and security risk rejection is allowed
  let defaultRejectionCategory: RejectionCategory;
  if (data.canUseSecurityRiskRejection) {
    if (props.rejectionCategory) {
      defaultRejectionCategory = props.rejectionCategory;
    } else {
      defaultRejectionCategory = data.fraudReport?.high_risk
        ? 'security_risk'
        : 'default';
    }
  } else {
    defaultRejectionCategory = 'default';
  }

  // if the backend default is a security concern and we can use the feature, set rejection category to security risk
  if (
    data.canUseSecurityRiskRejection &&
    data.rejectionReasons.defaultRejectionOption?.type === 'SECURITY CONCERN'
  ) {
    defaultRejectionCategory = 'security_risk';
  }

  let defaultRejectionReason: Item | undefined | null;
  if (
    data.canUseSecurityRiskRejection &&
    defaultRejectionCategory === 'security_risk' &&
    data.rejectionReasons.defaultRejectionOption?.type !== 'SECURITY CONCERN'
  ) {
    defaultRejectionReason = null;
  } else {
    defaultRejectionReason = data.rejectionReasons.defaultRejectionOption;
  }

  return (
    <>
      <Form<RejectForm>
        key={`${isRejectModalOpen}-${
          data.rejectionReasons.defaultRejectionOption?.value || 'new'
        }`}
        onSubmit={handleOnModalSubmit}
        defaultValues={{
          rejection_reason: defaultRejectionReason,
          currentDefaultRejectValue:
            data.rejectionReasons.defaultRejectionOption,
          rejection_category: defaultRejectionCategory,
          on_other_active_hiring_plans:
            data?.otherApplications?.numberOfOtherHiringPlans ?? 0 > 0,
          template: data.email.defaultTemplate,
          to: data.email.toOptions?.[0],
          reject_from_all: false,
          send_email: true,
          when:
            data.email.sendAt.defaultWhenOption ||
            data.email.sendAt.whenEmailOptions?.[0],
          sendDate: data.email.sendAt.defaultRejectionDate
            ? parseDateString(
                data.email.sendAt.defaultRejectionDate,
                data.dateFormatBirchPicker
              )
            : undefined,
          time: data.email.sendAt.defaultRejectionTimeOption,
        }}
      >
        <DataEntryModal
          size={'large'}
          isOpen={isRejectModalOpen}
          onDismiss={closeRejectModal}
          data-provides="reject-modal"
          ariaLabel={'reject-modal'}
          onSubmit={() => {
            return;
          }}
        >
          <DataEntryModal.Header onClick={closeRejectModal}>
            <SectionTitle large>
              <span className={classNames({ 'pii-blur': shouldMaskTitle })}>
                {title}
              </span>
            </SectionTitle>
          </DataEntryModal.Header>
          <DataEntryModal.Body>
            <ModalBodyWrapper>
              <RejectFormBody
                {...data}
                bulkMode={bulkMode}
                applicationsToRejectInBulkCount={
                  applicationsToRejectInBulkCount
                }
                cannotEmailReason={cannotEmailReason || ''}
                stageTypes={data?.stageTypes}
                canSendEmail={canSendEmail}
              />
            </ModalBodyWrapper>
          </DataEntryModal.Body>
          <DataEntryModal.Footer>
            <ModalFooter
              canSendEmail={canSendEmail}
              submitting={submitting}
              closeRejectModal={closeRejectModal}
              cannotEmailReason={cannotEmailReason || ''}
            />
          </DataEntryModal.Footer>
        </DataEntryModal>
        <BulkRejectConfirmationModal
          isOpen={isConfirmationModalOpen}
          close={closeConfirmationModal}
          closeRejectModal={closeRejectModal}
          applicationsToRejectInBulkCount={applicationsToRejectInBulkCount}
          onSubmit={onModalSubmit}
        />
      </Form>
    </>
  );
};

export default RejectModal;
